import { useState, useEffect, useCallback, useRef } from 'react';
import theme from 'config/theme';

const DEFAULT_TOP_BAR_HEIGHT = 48;
const DEFAULT_HEADER_HEIGHT = 56;
const DEFAULT_FILTERS_HEIGHT = 70;

export const useViewportGridHeight = (options = {}) => {
  const {
    headerSelector = '[data-testid="page-list-header"]',
    filtersSelector = '[data-testid="filters-section"]',
    additionalOffset = 0,
    customOffset = undefined,
  } = options;

  const topBarHeight =
    parseInt(theme.customValues.topBarHeight.replace('px', ''), 10) ||
    DEFAULT_TOP_BAR_HEIGHT;

  const [gridHeight, setGridHeight] = useState('calc(100vh - 300px)');
  const observerRef = useRef(null);
  const elementsFoundRef = useRef(false);

  const calculateHeight = useCallback(() => {
    if (customOffset) {
      setGridHeight(`calc(100vh - ${customOffset}px)`);
      return;
    }

    const attemptCalculation = (retries = 0) => {
      try {
        // Try to measure actual elements first
        const header = document.querySelector(headerSelector);
        const filters = document.querySelector(filtersSelector);

        let totalOffset = 0;

        if (header && filters) {
          // Ensure elements are fully rendered before measuring
          const headerRect = header.getBoundingClientRect();
          const filtersRect = filters.getBoundingClientRect();

          if (headerRect.height > 0 && filtersRect.height > 0) {
            const headerHeight = headerRect.height;
            const filtersHeight = filtersRect.height;
            totalOffset =
              headerHeight +
              filtersHeight +
              topBarHeight +
              additionalOffset +
              20; // +20px for padding/margins
            setGridHeight(`calc(100vh - ${totalOffset}px)`);
            elementsFoundRef.current = true;
            return;
          }
        }

        // If elements don't exist or don't have dimensions yet, retry up to 5 times
        if (retries < 5) {
          setTimeout(
            () => attemptCalculation(retries + 1),
            retries < 2 ? 100 : 300 + retries * 200
          );
        } else {
          totalOffset =
            DEFAULT_HEADER_HEIGHT +
            DEFAULT_FILTERS_HEIGHT +
            topBarHeight +
            additionalOffset +
            20;
          setGridHeight(`calc(100vh - ${totalOffset}px)`);
        }
      } catch (error) {
        const totalOffset =
          DEFAULT_HEADER_HEIGHT +
          DEFAULT_FILTERS_HEIGHT +
          topBarHeight +
          additionalOffset +
          20;
        setGridHeight(`calc(100vh - ${totalOffset}px)`);
      }
    };

    attemptCalculation();
  }, [
    customOffset,
    additionalOffset,
    headerSelector,
    filtersSelector,
    topBarHeight,
  ]);

  useEffect(() => {
    // Initial calculation with multiple attempts
    const initialTimer = setTimeout(calculateHeight, 100);
    const secondTimer = setTimeout(calculateHeight, 500);
    const thirdTimer = setTimeout(calculateHeight, 1000);

    // Set up MutationObserver to watch for DOM changes
    if (typeof window !== 'undefined') {
      observerRef.current = new MutationObserver((mutations) => {
        let shouldRecalculate = false;

        mutations.forEach((mutation) => {
          // Check if any added nodes match our selectors or their parents
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node;
              if (
                element.matches?.(headerSelector) ||
                element.matches?.(filtersSelector) ||
                element.querySelector?.(headerSelector) ||
                element.querySelector?.(filtersSelector)
              ) {
                shouldRecalculate = true;
              }
            }
          });
        });

        if (shouldRecalculate || !elementsFoundRef.current) {
          setTimeout(calculateHeight, 50);
        }
      });

      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    const handleResize = () => {
      calculateHeight();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(secondTimer);
      clearTimeout(thirdTimer);
      window.removeEventListener('resize', handleResize);

      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [calculateHeight, headerSelector, filtersSelector]);

  return { gridHeight };
};

export default useViewportGridHeight;
